import React, { useState } from 'react';
import { Play, Pause, Trash2, Edit, TrendingUp, Plus, Copy } from 'lucide-react';

const sampleAlgos = [
  {
    id: 1, name: 'Supertrend 30m', instrument: 'NIFTY 25JAN FUT',
    tf: '30m', status: 'live', trades: 12, pnl: '+₹18,450',
    winRate: '67%', deployed: '2025-01-10'
  },
  {
    id: 2, name: 'EMA Crossover', instrument: 'BANKNIFTY 25JAN FUT',
    tf: '15m', status: 'paused', trades: 5, pnl: '+₹4,200',
    winRate: '60%', deployed: '2025-01-12'
  },
  {
    id: 3, name: 'RSI Reversal', instrument: 'NIFTY 25JAN FUT',
    tf: '5m', status: 'stopped', trades: 0, pnl: '₹0',
    winRate: '—', deployed: '2025-01-15'
  },
];

const statusColors = {
  live:    { color: 'var(--bull)',   bg: 'var(--bull-dim)',    label: 'LIVE' },
  paused:  { color: 'var(--warn)',   bg: 'var(--warn-dim)',    label: 'PAUSED' },
  stopped: { color: 'var(--text-3)', bg: 'rgba(74,96,128,0.1)', label: 'STOPPED' },
};

export default function MyAlgos() {
  const [algos, setAlgos] = useState(sampleAlgos);

  const toggleStatus = (id) => {
    setAlgos(prev => prev.map(a => {
      if (a.id !== id) return a;
      return { ...a, status: a.status === 'live' ? 'paused' : 'live' };
    }));
  };

  const deleteAlgo = (id) => setAlgos(prev => prev.filter(a => a.id !== id));

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>My Algos</h1>
          <p>Manage and monitor your deployed strategies</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary">
            <Plus size={14} /> New Strategy
          </button>
        </div>
      </div>

      {/* Summary row */}
      <div className="grid-3 mb-24">
        <div className="stat-card bull">
          <div className="stat-label">Live Algos</div>
          <div className="stat-value bull">{algos.filter(a => a.status === 'live').length}</div>
        </div>
        <div className="stat-card warn">
          <div className="stat-label">Paused</div>
          <div className="stat-value" style={{color:'var(--warn)'}}>{algos.filter(a => a.status === 'paused').length}</div>
        </div>
        <div className="stat-card accent">
          <div className="stat-label">Total Strategies</div>
          <div className="stat-value accent">{algos.length}</div>
        </div>
      </div>

      {/* Algo cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {algos.map(algo => {
          const s = statusColors[algo.status];
          return (
            <div key={algo.id} className="card" style={{ padding: '18px 20px' }}>
              <div className="flex-center justify-between">
                {/* Left info */}
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div>
                    <div className="flex-center gap-8 mb-16" style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-1)' }}>{algo.name}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 7px',
                        borderRadius: 4, background: s.bg, color: s.color,
                        fontFamily: 'var(--mono)'
                      }}>{s.label}</span>
                    </div>
                    <div className="flex-center gap-16" style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text-3)' }}>
                      <span>{algo.instrument}</span>
                      <span>TF: {algo.tf}</span>
                      <span>Trades: {algo.trades}</span>
                      <span>Win Rate: {algo.winRate}</span>
                      <span>Since: {algo.deployed}</span>
                    </div>
                  </div>
                </div>

                {/* Right — P&L + actions */}
                <div className="flex-center gap-16">
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>Total P&L</div>
                    <div style={{
                      fontSize: 20, fontFamily: 'var(--mono)', fontWeight: 700,
                      color: algo.pnl.startsWith('+') ? 'var(--bull)' : algo.pnl === '₹0' ? 'var(--text-2)' : 'var(--bear)'
                    }}>{algo.pnl}</div>
                  </div>

                  <div className="flex-center gap-8">
                    <button className="btn btn-outline btn-sm" title="Edit">
                      <Edit size={13} />
                    </button>
                    <button className="btn btn-outline btn-sm" title="Duplicate">
                      <Copy size={13} />
                    </button>
                    <button
                      className={`btn btn-sm ${algo.status === 'live' ? 'btn-bear' : 'btn-bull'}`}
                      onClick={() => toggleStatus(algo.id)}
                    >
                      {algo.status === 'live'
                        ? <><Pause size={13} /> Pause</>
                        : <><Play size={13} /> Start</>
                      }
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => deleteAlgo(algo.id)}
                      style={{ color: 'var(--bear)', borderColor: 'var(--bear)' }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {algos.length === 0 && (
        <div className="empty-state">
          <TrendingUp size={48} />
          <h3>No strategies yet</h3>
          <p>Go to Algo Builder to create your first strategy</p>
        </div>
      )}
    </div>
  );
}
